import React, { useState, useRef, useEffect } from 'react';

import {
  IconButton,
  Popper,
  Badge,
  Link,
  Paper,
  MenuList,
  ClickAwayListener
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, TextField, Translate } from '@hydra/common/components';

import { useI18n } from '@hydra/common/customHooks';
import { sendEvent } from '@hydra/utils/gtm';

const useStyles = makeStyles(theme => ({
  textField: {
    maxWidth: '109px'
  },
  textFieldUrl: {
    width: '245px'
  },
  menuList: {
    padding: theme.spacing(3, 3, 2, 3),
    width: '294px'
  },
  middle: {
    paddingBottom: theme.spacing(1)
  },
  label: {
    fontWeight: theme.typography.fontWeightSemibold,
    marginBottom: theme.spacing(0.5)
  },
  filter: {
    paddingBottom: theme.spacing(1.5),
    display: 'flex'
  },
  clearFilterWrapper: {
    textAlign: 'center',
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(1.5)
  },
  clearFilter: {
    color: theme.palette.error.main,
    fontWeight: theme.typography.fontWeightSemibold,
    cursor: 'pointer'
  },
  divider: {
    fontWeight: theme.typography.fontWeightLight,
    fontSize: '22px',
    margin: theme.spacing(1)
  }
}));

interface Filter {
  from: string;
  to: string;
}

interface RankedKeywordsFilter {
  volumeFilter: Filter;
  setVolumeFilter: (data: Filter) => void;
  positionFilter: Filter;
  setPositionFilter: (data: Filter) => void;
  urlFilter: string,
  setUrlFilter: (value: string) => void;
}

export default function RankedKeywordsFilter (props: RankedKeywordsFilter) {
  const classes = useStyles();
  const { translate } = useI18n();
  const {
    volumeFilter,
    setVolumeFilter,
    positionFilter,
    setPositionFilter,
    urlFilter,
    setUrlFilter
  } = props;

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const hasFiltersApplied = () => {
    return (
      volumeFilter.from ||
      volumeFilter.to ||
      positionFilter.from ||
      positionFilter.to ||
      urlFilter
    );
  };

  const handleToggle = () => {
    if (!open) {
      sendEvent('keywordtool-addkeyword-rankedkeywordsfilter-btn');
    }
    setOpen(d => !d);
  };

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false && anchorRef.current) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleChangeVolumeFilter = (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setVolumeFilter({
      ...volumeFilter,
      [name]: event.target.value
    });
  };

  const handleChangePositionFilter = (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setPositionFilter({
      ...positionFilter,
      [name]: event.target.value
    });
  };

  const handleChangeUrl = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrlFilter(event.target.value);
  };

  const handleClearFilter = () => {
    setVolumeFilter({ from: '', to: '' });
    setPositionFilter({ from: '', to: '' });
    setUrlFilter('');
  };

  return (
    <>
      <IconButton ref={anchorRef} size='medium' onClick={handleToggle}>
        <Badge variant='dot' invisible={!hasFiltersApplied()} color='primary'>
          <Icon type='filter' size='md' />
        </Badge>
      </IconButton>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement='bottom-end'
        transition
      >
        <Paper>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList
              disablePadding
              autoFocusItem={open}
              className={classes.menuList}
            >
              <Translate content='filter' className={classes.filter} />
              <div className={classes.middle}>
                <Translate
                  content='url'
                  component='div'
                  className={classes.label}
                />
                <TextField
                  className={classes.textFieldUrl}
                  margin='none'
                  disableShadow
                  placeholder={translate('url')}
                  value={urlFilter}
                  onChange={handleChangeUrl}
                />
              </div>
              <div className={classes.middle}>
                <Translate
                  content='serp_front_volume'
                  component='div'
                  className={classes.label}
                />
                <TextField
                  className={classes.textField}
                  margin='none'
                  disableShadow
                  placeholder={translate('serp_ranked_filter_from')}
                  value={volumeFilter.from}
                  onChange={handleChangeVolumeFilter('from')}
                  type='number'
                />
                <span className={classes.divider}>–</span>
                <TextField
                  className={classes.textField}
                  margin='none'
                  disableShadow
                  placeholder={translate('serp_ranked_filter_to')}
                  value={volumeFilter.to}
                  onChange={handleChangeVolumeFilter('to')}
                  type='number'
                />
              </div>
              <div>
                <Translate
                  content='serp_ranked_keywords_position'
                  component='div'
                  className={classes.label}
                />
                <TextField
                  className={classes.textField}
                  margin='none'
                  disableShadow
                  placeholder={translate('serp_ranked_filter_from')}
                  value={positionFilter.from}
                  onChange={handleChangePositionFilter('from')}
                  type='number'
                />
                <span className={classes.divider}>–</span>
                <TextField
                  className={classes.textField}
                  margin='none'
                  disableShadow
                  placeholder={translate('serp_ranked_filter_to')}
                  value={positionFilter.to}
                  onChange={handleChangePositionFilter('to')}
                  type='number'
                />
                <div className={classes.clearFilterWrapper}>
                  <Link
                    onClick={handleClearFilter}
                    className={classes.clearFilter}
                  >
                    <Translate content='filter_clear_all_fields' />
                  </Link>
                </div>
              </div>
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </>
  );
}
